import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { isNativePlatform } from '@/services/platformService';

const AmbassadorTerms = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isNativePlatform()) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  if (isNativePlatform()) return null;

  const sections = [
    { id: 'nature', num: '1' },
    { id: 'environment', num: '2' },
    { id: 'commissions', num: '3' },
    { id: 'payments', num: '4' },
    { id: 'promotion', num: '5' },
    { id: 'modifications', num: '6' },
    { id: 'termination', num: '7' },
    { id: 'confidentiality', num: '8' },
    { id: 'liability', num: '9' },
    { id: 'general', num: '10' },
  ];

  const tiers = ['mega', 'macro', 'micro', 'nano'] as const;

  const tierData: Record<string, { standard: [string, string, string]; premium: [string, string, string]; ultimate: [string, string, string] }> = {
    mega: {
      standard: ['$76.99', '$9.90', '$0.83'],
      premium: ['$109.89', '$10.99', '$0.92'],
      ultimate: ['$219.89', '$16.00', '$1.33'],
    },
    macro: {
      standard: ['$76.99', '$8.50', '$0.71'],
      premium: ['$109.89', '$9.39', '$0.78'],
      ultimate: ['$219.89', '$14.20', '$1.18'],
    },
    micro: {
      standard: ['$76.99', '$7.10', '$0.59'],
      premium: ['$109.89', '$7.79', '$0.65'],
      ultimate: ['$219.89', '$12.40', '$1.03'],
    },
    nano: {
      standard: ['$76.99', '$5.70', '$0.48'],
      premium: ['$109.89', '$6.19', '$0.52'],
      ultimate: ['$219.89', '$11.80', '$0.98'],
    },
  };

  const renderCommissionTable = (tier: string) => (
    <div key={tier} className="mb-6">
      <h4 className="text-md font-semibold text-purple-300 mb-3">
        {t(`ambassador.terms.sections.commissions.tiers.${tier}`)}
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-purple-900/40">
              <th className="border border-purple-500/30 px-4 py-3 text-left text-purple-200">{t('ambassador.terms.sections.commissions.table.plan')}</th>
              <th className="border border-purple-500/30 px-4 py-3 text-left text-purple-200">{t('ambassador.terms.sections.commissions.table.price')}</th>
              <th className="border border-purple-500/30 px-4 py-3 text-left text-purple-200">{t('ambassador.terms.sections.commissions.table.cpa')}</th>
              <th className="border border-purple-500/30 px-4 py-3 text-left text-purple-200">{t('ambassador.terms.sections.commissions.table.monthly')}</th>
            </tr>
          </thead>
          <tbody>
            {(['standard', 'premium', 'ultimate'] as const).map((plan, idx) => (
              <tr key={plan} className={idx % 2 === 0 ? 'bg-purple-900/10' : 'bg-purple-900/20'}>
                <td className="border border-purple-500/20 px-4 py-3">{t(`ambassador.terms.sections.commissions.plans.${plan}`)}</td>
                <td className="border border-purple-500/20 px-4 py-3 text-gray-300">{tierData[tier][plan][0]}</td>
                <td className="border border-purple-500/20 px-4 py-3 text-green-400 font-semibold">{tierData[tier][plan][1]}</td>
                <td className="border border-purple-500/20 px-4 py-3 text-green-400">{tierData[tier][plan][2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-gray-200">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 text-gray-400 hover:text-white hover:bg-white/10"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </Button>

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900/80 to-indigo-900/80 border border-purple-500/30 p-8 rounded-t-2xl">
          <p className="text-purple-300 text-sm font-medium tracking-widest uppercase mb-2">PETIUS.IO</p>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {t('ambassador.terms.title')}
          </h1>
          <p className="text-purple-200/70 text-sm">
            {t('ambassador.terms.version')}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-0">
          {/* Table of Contents - sticky sidebar */}
          <nav className="lg:sticky lg:top-8 lg:self-start bg-[#12121a] border-x border-b lg:border-b-0 lg:rounded-bl-2xl border-purple-500/20 p-6 lg:w-64 lg:min-w-[16rem] shrink-0">
            <h2 className="text-sm font-semibold text-purple-300 uppercase tracking-wider mb-4">
              {t('ambassador.terms.toc')}
            </h2>
            <ul className="space-y-2">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#section-${s.id}`}
                    className="text-sm text-gray-400 hover:text-purple-300 transition-colors block py-1"
                  >
                    {t(`ambassador.terms.sections.${s.id}.title`)}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content */}
          <div className="bg-[#12121a] border border-purple-500/20 border-l-0 p-8 rounded-b-2xl lg:rounded-bl-none lg:rounded-br-2xl flex-1 prose prose-invert max-w-none prose-headings:text-white prose-p:text-gray-300 prose-li:text-gray-300 prose-strong:text-white">
            
            <p className="text-gray-300 leading-relaxed mb-8">
              {t('ambassador.terms.intro')}
            </p>

            {/* Section 1 */}
            <section id="section-nature" className="mb-10">
              <h2 className="text-2xl font-bold mb-4">{t('ambassador.terms.sections.nature.title')}</h2>
              <p>{t('ambassador.terms.sections.nature.content')}</p>
              
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.nature.tax.title')}</h3>
              <p><strong>{t('ambassador.terms.sections.nature.tax.colombia')}</strong></p>
              <p>{t('ambassador.terms.sections.nature.tax.colombiaContent')}</p>
              <p className="mt-4"><strong>{t('ambassador.terms.sections.nature.tax.international')}</strong></p>
              <p>{t('ambassador.terms.sections.nature.tax.internationalContent')}</p>
              
              <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4 my-4">
                <p className="text-amber-300 font-semibold text-sm">⚠ {t('ambassador.terms.sections.nature.tax.riskTitle')}</p>
                <p className="text-gray-300 text-sm mt-2">{t('ambassador.terms.sections.nature.tax.riskContent')}</p>
              </div>

              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 my-4">
                <p className="text-blue-300 font-semibold text-sm">ℹ {t('ambassador.terms.sections.nature.tax.deductTitle')}</p>
                <p className="text-gray-300 text-sm mt-2">{t('ambassador.terms.sections.nature.tax.deductContent')}</p>
              </div>

              <p className="mt-4">{t('ambassador.terms.sections.nature.tax.inoponibilidad')}</p>

              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.nature.disputes.title')}</h3>
              <p>{t('ambassador.terms.sections.nature.disputes.content')}</p>
            </section>

            {/* Section 2 */}
            <section id="section-environment" className="mb-10">
              <h2 className="text-2xl font-bold mb-4">{t('ambassador.terms.sections.environment.title')}</h2>
              <p>{t('ambassador.terms.sections.environment.content')}</p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>{t('ambassador.terms.sections.environment.items.adult')}</li>
                <li>{t('ambassador.terms.sections.environment.items.fraud')}</li>
                <li>{t('ambassador.terms.sections.environment.items.violence')}</li>
                <li>{t('ambassador.terms.sections.environment.items.minors')}</li>
              </ul>
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.environment.responsibility.title')}</h3>
              <p>{t('ambassador.terms.sections.environment.responsibility.content')}</p>
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 my-4">
                <p className="text-red-300 font-semibold text-sm">⚠ {t('ambassador.terms.sections.environment.breach.title')}</p>
                <p className="text-gray-300 text-sm mt-2">{t('ambassador.terms.sections.environment.breach.content')}</p>
              </div>
            </section>

            {/* Section 3 - Commissions */}
            <section id="section-commissions" className="mb-10">
              <h2 className="text-2xl font-bold mb-4">{t('ambassador.terms.sections.commissions.title')}</h2>
              <p>{t('ambassador.terms.sections.commissions.content')}</p>

              <div className="my-6 space-y-8">
                {tiers.map((tier) => renderCommissionTable(tier))}
              </div>

              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.commissions.channels.title')}</h3>
              <p>{t('ambassador.terms.sections.commissions.channels.content')}</p>
              <p className="mt-4">{t('ambassador.terms.sections.commissions.channels.exclusions')}</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>{t('ambassador.terms.sections.commissions.channels.items.free')}</li>
                <li>{t('ambassador.terms.sections.commissions.channels.items.renewals')}</li>
                <li>{t('ambassador.terms.sections.commissions.channels.items.external')}</li>
                <li>{t('ambassador.terms.sections.commissions.channels.items.refunds')}</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.commissions.monthly.title')}</h3>
              <p>{t('ambassador.terms.sections.commissions.monthly.content')}</p>

              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.commissions.changes.title')}</h3>
              <p>{t('ambassador.terms.sections.commissions.changes.content')}</p>
            </section>

            {/* Section 4 */}
            <section id="section-payments" className="mb-10">
              <h2 className="text-2xl font-bold mb-4">{t('ambassador.terms.sections.payments.title')}</h2>
              <h3 className="text-lg font-semibold mt-4 mb-3">{t('ambassador.terms.sections.payments.hold.title')}</h3>
              <p>{t('ambassador.terms.sections.payments.hold.content')}</p>
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.payments.threshold.title')}</h3>
              <p>{t('ambassador.terms.sections.payments.threshold.content')}</p>
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.payments.method.title')}</h3>
              <p>{t('ambassador.terms.sections.payments.method.content')}</p>
              <p className="mt-4">{t('ambassador.terms.sections.payments.method.alternatives')}</p>
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 my-4">
                <p className="text-blue-300 font-semibold text-sm">ℹ {t('ambassador.terms.sections.payments.method.verificationTitle')}</p>
                <p className="text-gray-300 text-sm mt-2">{t('ambassador.terms.sections.payments.method.verification')}</p>
              </div>
            </section>

            {/* Section 5 */}
            <section id="section-promotion" className="mb-10">
              <h2 className="text-2xl font-bold mb-4">{t('ambassador.terms.sections.promotion.title')}</h2>
              <p>{t('ambassador.terms.sections.promotion.content')}</p>
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.promotion.discounts.title')}</h3>
              <p>{t('ambassador.terms.sections.promotion.discounts.content')}</p>
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.promotion.spam.title')}</h3>
              <p>{t('ambassador.terms.sections.promotion.spam.content')}</p>
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.promotion.brand.title')}</h3>
              <p>{t('ambassador.terms.sections.promotion.brand.content')}</p>
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.promotion.limit.title')}</h3>
              <p>{t('ambassador.terms.sections.promotion.limit.content')}</p>
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.promotion.transparency.title')}</h3>
              <p>{t('ambassador.terms.sections.promotion.transparency.content')}</p>
            </section>

            {/* Section 6 */}
            <section id="section-modifications" className="mb-10">
              <h2 className="text-2xl font-bold mb-4">{t('ambassador.terms.sections.modifications.title')}</h2>
              <p>{t('ambassador.terms.sections.modifications.content')}</p>
            </section>

            {/* Section 7 */}
            <section id="section-termination" className="mb-10">
              <h2 className="text-2xl font-bold mb-4">{t('ambassador.terms.sections.termination.title')}</h2>
              <p>{t('ambassador.terms.sections.termination.audit')}</p>
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.termination.cause.title')}</h3>
              <p>{t('ambassador.terms.sections.termination.cause.content')}</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>{t('ambassador.terms.sections.termination.cause.items.fraud')}</li>
                <li>{t('ambassador.terms.sections.termination.cause.items.refunds')}</li>
                <li>{t('ambassador.terms.sections.termination.cause.items.violation')}</li>
              </ul>
              <p className="mt-4">{t('ambassador.terms.sections.termination.cause.investigation')}</p>
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.termination.ordinary.title')}</h3>
              <p>{t('ambassador.terms.sections.termination.ordinary.content')}</p>
              <h3 className="text-lg font-semibold mt-6 mb-3">{t('ambassador.terms.sections.termination.voluntary.title')}</h3>
              <p>{t('ambassador.terms.sections.termination.voluntary.content')}</p>
            </section>

            {/* Section 8 */}
            <section id="section-confidentiality" className="mb-10">
              <h2 className="text-2xl font-bold mb-4">{t('ambassador.terms.sections.confidentiality.title')}</h2>
              <p>{t('ambassador.terms.sections.confidentiality.content')}</p>
              <p className="mt-4">{t('ambassador.terms.sections.confidentiality.data')}</p>
            </section>

            {/* Section 9 */}
            <section id="section-liability" className="mb-10">
              <h2 className="text-2xl font-bold mb-4">{t('ambassador.terms.sections.liability.title')}</h2>
              <p>{t('ambassador.terms.sections.liability.content')}</p>
            </section>

            {/* Section 10 */}
            <section id="section-general" className="mb-10">
              <h2 className="text-2xl font-bold mb-4">{t('ambassador.terms.sections.general.title')}</h2>
              <p><strong>{t('ambassador.terms.sections.general.integration.title')}:</strong> {t('ambassador.terms.sections.general.integration.content')}</p>
              <p className="mt-3"><strong>{t('ambassador.terms.sections.general.severability.title')}:</strong> {t('ambassador.terms.sections.general.severability.content')}</p>
              <p className="mt-3"><strong>{t('ambassador.terms.sections.general.noWaiver.title')}:</strong> {t('ambassador.terms.sections.general.noWaiver.content')}</p>
              <p className="mt-3"><strong>{t('ambassador.terms.sections.general.assignment.title')}:</strong> {t('ambassador.terms.sections.general.assignment.content')}</p>
              <p className="mt-3"><strong>{t('ambassador.terms.sections.general.communications.title')}:</strong> {t('ambassador.terms.sections.general.communications.content')}</p>
            </section>

            <div className="border-t border-purple-500/20 pt-6 mt-10 text-center text-gray-500 text-sm">
              © 2026 Petius.io — {t('ambassador.terms.allRightsReserved')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmbassadorTerms;
